/**
 * Vínculos entre partes (pessoas de uma empresa, papéis num condomínio, donos de um bem). Modo
 * leitura: lista o que `endpointGet` devolver, num shape que varia por entidade. Mostra, além do
 * nome da outra ponta, o que a liga: o rótulo do vínculo quando existe, senão o cargo/papel,
 * senão a natureza. A criação tem corpo específico por entidade, então cada uma a expõe como
 * painel `custom`.
 */
import { Link2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { estadoDaLista } from '@/components/ui/estado';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Chip } from '@/components/ui/misc';
import { api } from '@/lib/api';

type LinhaVinculo = Record<string, unknown> & { id?: string };

const CHAVES_NOME = ['nome', 'razao_social', 'nome_fantasia'];

/** Pega o primeiro rótulo humano disponível numa linha de shape desconhecido.
 * O nome pode estar embutido na outra ponta do vínculo (`pessoa`, `empresa`, `owner`,
 * `condominio`) — é o caso do GET de sócios da empresa (`{ pessoa: { nome } }`) — ou no topo.
 * A ponta embutida vem PRIMEIRO, e `rotulo` saiu das chaves de nome: desde que o vínculo
 * pessoa↔empresa passou a ter rótulo próprio ("Paciente desde 2019"), procurá-lo aqui fazia o
 * nome do vínculo aparecer no lugar do nome da pessoa. */
function nomeDaPonta(l: LinhaVinculo): string {
  const candidatos: unknown[] = [l.pessoa, l.empresa, l.owner, l.condominio, l];
  for (const alvo of candidatos) {
    if (alvo && typeof alvo === 'object') {
      for (const k of CHAVES_NOME) {
        const v = (alvo as Record<string, unknown>)[k];
        if (typeof v === 'string' && v) return v;
      }
    }
  }
  return '—';
}

/** Texto de um cargo/papel que pode vir como string OU como objeto `{ nome }`. */
function textoChip(v: unknown): string | null {
  if (typeof v === 'string' && v) return v;
  if (v && typeof v === 'object') {
    const nome = (v as Record<string, unknown>).nome;
    if (typeof nome === 'string' && nome) return nome;
  }
  return null;
}

export function PainelVinculos({
  titulo,
  endpointGet,
  endpointPost: _endpointPost,
  ownerId: _ownerId,
}: {
  titulo: string;
  endpointGet?: string;
  endpointPost: string;
  ownerId: string;
}) {
  const [linhas, setLinhas] = useState<LinhaVinculo[]>([]);
  const [carregando, setCarregando] = useState(!!endpointGet);

  useEffect(() => {
    if (!endpointGet) return;
    let vivo = true;
    setCarregando(true);
    void api.get<LinhaVinculo[]>(endpointGet).then((r) => {
      if (!vivo) return;
      setCarregando(false);
      if (r.ok) setLinhas(r.dados);
    });
    return () => {
      vivo = false;
    };
  }, [endpointGet]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Link2 className="size-4 text-accent" />
          {titulo}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        {!endpointGet ? (
          <p className="tipo-corpo-pequeno text-text-tertiary">
            A listagem deste vínculo chega com a próxima leva do backend.
          </p>
        ) : (
          estadoDaLista(carregando, linhas.length === 0, 'Nenhum vínculo ainda.') ?? (
          linhas.map((l, i) => (
            <div
              key={l.id ?? i}
              className="flex flex-wrap items-center gap-2 rounded-md border border-border px-3 py-2"
            >
              <span className="text-[15px] text-text">{nomeDaPonta(l)}</span>
              {textoChip(l.rotulo) && <Chip aceso>{textoChip(l.rotulo)}</Chip>}
              {textoChip(l.cargo) && <Chip>{textoChip(l.cargo)}</Chip>}
              {textoChip(l.papel) && <Chip>{textoChip(l.papel)}</Chip>}
              {!textoChip(l.rotulo) && !textoChip(l.cargo) && textoChip(l.natureza) && (
                <Chip>{textoChip(l.natureza)}</Chip>
              )}
            </div>
          ))
        ))}
      </CardContent>
    </Card>
  );
}
